// ── Keybind Manager ──
// Configurable keybinding system with localStorage persistence.
// Supports rebinding, conflict detection (swap), reset to defaults,
// and change listeners for UI updates.

export interface Keybind {
  /** Unique action identifier (e.g. 'forward', 'fire', 'buyMenu'). */
  action: string;
  /** KeyboardEvent.code or 'Mouse0'/'Mouse1'/'Mouse2' for mouse buttons. */
  key: string;
  /** UI grouping category. */
  category: 'Movement' | 'Combat' | 'Weapons' | 'Utility';
  /** Human-readable action name for the UI. */
  displayName: string;
}

export interface RebindResult {
  success: boolean;
  /** If the new key was already bound, this contains the conflicting keybind (which was swapped). */
  conflict?: Keybind;
}

const STORAGE_KEY = 'counter-pump-keybinds';

// ── Default keybindings ──
// Key values use KeyboardEvent.code format, or Mouse0/1/2 for mouse buttons.
export const DEFAULT_KEYBINDS: readonly Keybind[] = [
  // Movement
  { action: 'forward',   key: 'KeyW',         category: 'Movement', displayName: 'Forward' },
  { action: 'backward',  key: 'KeyS',         category: 'Movement', displayName: 'Backward' },
  { action: 'left',      key: 'KeyA',         category: 'Movement', displayName: 'Strafe Left' },
  { action: 'right',     key: 'KeyD',         category: 'Movement', displayName: 'Strafe Right' },
  { action: 'jump',      key: 'Space',        category: 'Movement', displayName: 'Jump' },
  { action: 'crouch',    key: 'ControlLeft',  category: 'Movement', displayName: 'Crouch' },
  { action: 'walk',      key: 'ShiftLeft',    category: 'Movement', displayName: 'Walk' },

  // Combat
  { action: 'fire',      key: 'Mouse0',       category: 'Combat',   displayName: 'Fire' },
  { action: 'aim',       key: 'Mouse2',       category: 'Combat',   displayName: 'Aim / Scope' },
  { action: 'reload',    key: 'KeyR',         category: 'Combat',   displayName: 'Reload' },
  { action: 'inspect',   key: 'KeyF',         category: 'Combat',   displayName: 'Inspect Weapon' },

  // Weapons
  { action: 'primary',   key: 'Digit1',       category: 'Weapons',  displayName: 'Primary' },
  { action: 'secondary', key: 'Digit2',       category: 'Weapons',  displayName: 'Secondary' },
  { action: 'melee',     key: 'Digit3',       category: 'Weapons',  displayName: 'Melee' },
  { action: 'grenades',  key: 'Digit4',       category: 'Weapons',  displayName: 'Grenades' },
  { action: 'bomb',      key: 'Digit5',       category: 'Weapons',  displayName: 'Bomb' },
  { action: 'cycleGrenade', key: 'KeyG',      category: 'Weapons',  displayName: 'Cycle Grenade' },
  { action: 'drop',      key: 'KeyH',         category: 'Weapons',  displayName: 'Drop Weapon' },

  // Utility
  { action: 'buyMenu',     key: 'KeyB',       category: 'Utility',  displayName: 'Buy Menu' },
  { action: 'scoreboard',  key: 'Tab',        category: 'Utility',  displayName: 'Scoreboard' },
  { action: 'chat',        key: 'KeyT',       category: 'Utility',  displayName: 'Chat' },
  { action: 'teamChat',    key: 'KeyY',       category: 'Utility',  displayName: 'Team Chat' },
  { action: 'radioA',      key: 'KeyZ',       category: 'Utility',  displayName: 'Radio A' },
  { action: 'radioB',      key: 'KeyX',       category: 'Utility',  displayName: 'Radio B' },
  { action: 'radioC',      key: 'KeyC',       category: 'Utility',  displayName: 'Radio C' },
  { action: 'interact',    key: 'KeyE',       category: 'Utility',  displayName: 'Interact / Plant / Defuse' },
  { action: 'togglePerspective', key: 'KeyV', category: 'Utility',  displayName: 'Toggle FPP/TPP' },
  { action: 'teamSelect',  key: 'KeyM',       category: 'Utility',  displayName: 'Team Select' },
  { action: 'leaderboard', key: 'KeyL',       category: 'Utility',  displayName: 'Leaderboard' },
  { action: 'netGraph',    key: 'F10',        category: 'Utility',  displayName: 'Net Graph' },
  { action: 'xray',        key: 'KeyJ',       category: 'Utility',  displayName: 'X-Ray (Spectator)' },
  { action: 'autoDirector', key: 'KeyP',      category: 'Utility',  displayName: 'Auto-Director' },
  { action: 'pushToTalk',  key: 'CapsLock',    category: 'Utility',  displayName: 'Push to Talk' },
  { action: 'editHudLayout', key: 'F9',        category: 'Utility',  displayName: 'Edit Touch HUD Layout' },
  { action: 'pingWheel',   key: 'KeyQ',        category: 'Utility',  displayName: 'Ping Wheel' },
];

export class KeybindManager {
  private keybinds: Keybind[];
  private listeners: Array<() => void> = [];
  /** Fast lookup: action -> index in keybinds array. */
  private actionIndex = new Map<string, number>();
  /** Fast lookup: key -> action. */
  private keyIndex = new Map<string, string>();

  constructor() {
    this.keybinds = this.load();
    this.rebuildIndices();
  }

  // ── Queries ──

  /** Get all keybinds. */
  getAll(): readonly Keybind[] {
    return this.keybinds;
  }

  /** Get keybinds filtered by category. */
  getByCategory(category: string): Keybind[] {
    return this.keybinds.filter((b) => b.category === category);
  }

  /** Get keybind for a specific action. */
  getKeybind(action: string): Keybind | undefined {
    const idx = this.actionIndex.get(action);
    return idx !== undefined ? this.keybinds[idx] : undefined;
  }

  /** Get the key code currently bound to an action. */
  getKeyForAction(action: string): string | undefined {
    return this.getKeybind(action)?.key;
  }

  /** Check if a key code matches the keybind for an action. */
  isAction(action: string, keyCode: string): boolean {
    const bind = this.getKeybind(action);
    return bind !== undefined && bind.key === keyCode;
  }

  // ── Mutation ──

  /** Rebind an action to a new key. If the key is already used, swaps the bindings. */
  rebind(action: string, newKey: string): RebindResult {
    const idx = this.actionIndex.get(action);
    if (idx === undefined) {
      return { success: false };
    }

    const current = this.keybinds[idx];

    // Same key — no-op success
    if (current.key === newKey) {
      return { success: true };
    }

    // Check for conflict
    const conflictAction = this.keyIndex.get(newKey);
    let conflict: Keybind | undefined;

    if (conflictAction !== undefined && conflictAction !== action) {
      const conflictIdx = this.actionIndex.get(conflictAction)!;
      conflict = { ...this.keybinds[conflictIdx] };
      // Swap: give the conflicting action the old key
      this.keybinds[conflictIdx] = { ...this.keybinds[conflictIdx], key: current.key };
    }

    // Apply new key
    this.keybinds[idx] = { ...current, key: newKey };

    this.rebuildIndices();
    this.save();
    this.notifyListeners();

    return { success: true, conflict };
  }

  /** Reset all keybinds to their default values. */
  resetToDefaults(): void {
    this.keybinds = DEFAULT_KEYBINDS.map((b) => ({ ...b }));
    this.rebuildIndices();
    this.save();
    this.notifyListeners();
  }

  // ── Change listeners ──

  /** Register a callback that fires when any keybind changes. */
  onChange(fn: () => void): void {
    this.listeners.push(fn);
  }

  /** Remove a previously registered change listener. */
  offChange(fn: () => void): void {
    this.listeners = this.listeners.filter((f) => f !== fn);
  }

  private notifyListeners(): void {
    for (const fn of this.listeners) fn();
  }

  // ── Persistence ──

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.keybinds));
    } catch {
      // localStorage full or unavailable — silently ignore
    }
  }

  private load(): Keybind[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw) as Keybind[];
        if (Array.isArray(saved)) {
          // Merge: use saved keys for known actions, add any new default actions
          return this.mergeWithDefaults(saved);
        }
      }
    } catch {
      // Corrupted data — fall back to defaults
    }
    return DEFAULT_KEYBINDS.map((b) => ({ ...b }));
  }

  /** Merge saved keybinds with defaults so new actions added in updates are included. */
  private mergeWithDefaults(saved: Keybind[]): Keybind[] {
    const savedMap = new Map<string, Keybind>();
    for (const s of saved) {
      if (s.action && s.key) {
        savedMap.set(s.action, s);
      }
    }

    return DEFAULT_KEYBINDS.map((def) => {
      const s = savedMap.get(def.action);
      if (s) {
        // Use saved key but ensure category/displayName stay current
        return { ...def, key: s.key };
      }
      return { ...def };
    });
  }

  // ── Index management ──

  private rebuildIndices(): void {
    this.actionIndex.clear();
    this.keyIndex.clear();
    for (let i = 0; i < this.keybinds.length; i++) {
      const b = this.keybinds[i];
      this.actionIndex.set(b.action, i);
      // Skip unbound ('') keys — they aren't lookupable by empty code and would
      // otherwise collide with each other in the reverse-lookup map.
      if (b.key !== '') {
        this.keyIndex.set(b.key, b.action);
      }
    }
  }

  // ── Static helpers ──

  /** Convert a KeyboardEvent.code or Mouse button to a human-readable string. */
  static formatKeyName(code: string): string {
    if (!code) return 'Unbound';
    // Mouse buttons
    if (code === 'Mouse0') return 'Mouse 1';
    if (code === 'Mouse1') return 'Mouse 3';
    if (code === 'Mouse2') return 'Mouse 2';

    // Modifier keys
    if (code === 'ShiftLeft') return 'L-Shift';
    if (code === 'ShiftRight') return 'R-Shift';
    if (code === 'ControlLeft') return 'L-Ctrl';
    if (code === 'ControlRight') return 'R-Ctrl';
    if (code === 'AltLeft') return 'L-Alt';
    if (code === 'AltRight') return 'R-Alt';
    if (code === 'MetaLeft') return 'L-Meta';
    if (code === 'MetaRight') return 'R-Meta';

    // Letter keys (KeyW -> W)
    if (code.startsWith('Key') && code.length === 4) {
      return code.charAt(3);
    }

    // Digit keys (Digit1 -> 1)
    if (code.startsWith('Digit') && code.length === 6) {
      return code.charAt(5);
    }

    // Arrow keys
    if (code.startsWith('Arrow')) {
      return code.slice(5); // ArrowUp -> Up
    }

    // Numpad
    if (code.startsWith('Numpad')) {
      return 'Num ' + code.slice(6);
    }

    // Everything else: return as-is (Space, Tab, Enter, Escape, etc.)
    return code;
  }
}

/** Singleton keybind manager instance. */
export const keybindManager = new KeybindManager();
